import React, { useEffect, useId } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { Refresh } from '@mui/icons-material';
import { useMembers } from '@/hooks/useMembers';

interface MemberIdentifierSelectProps {
  value: string;
  onChange: (value: string) => void;
  open?: boolean;
  error?: string;
  helperText?: string;
  label?: string;
  required?: boolean;
  margin?: 'none' | 'dense' | 'normal';
}

export const MemberIdentifierSelect: React.FC<MemberIdentifierSelectProps> = ({
  value,
  onChange,
  open = false,
  error,
  helperText = 'Select a member from the available list. Disabled members are not accessible with your current roles.',
  label = 'Member Identifier',
  required = false,
  margin = 'dense',
}) => {
  const { members, membersLoading, filteredMembers, fetchMembers } = useMembers();
  const selectId = useId();
  const labelId = `${selectId}-label`;

  useEffect(() => {
    if (open && members.length === 0) {
      void fetchMembers();
    }
  }, [fetchMembers, members.length, open]);

  const renderedLabel = required ? `${label} *` : label;

  return (
    <FormControl fullWidth margin={margin} sx={{ mb: 2 }} error={!!error}>
      <InputLabel id={labelId}>{renderedLabel}</InputLabel>
      <Select
        id={selectId}
        labelId={labelId}
        value={value}
        label={renderedLabel}
        onChange={(event: SelectChangeEvent<string>) => onChange(event.target.value)}
        disabled={membersLoading}
        endAdornment={
          <IconButton onClick={() => void fetchMembers()} disabled={membersLoading} sx={{ mr: 3 }}>
            <Tooltip title="Refresh members">
              <Refresh />
            </Tooltip>
          </IconButton>
        }
      >
        {membersLoading && <MenuItem disabled>Loading members...</MenuItem>}
        {!membersLoading && filteredMembers.length === 0 && <MenuItem disabled>No members available</MenuItem>}
        {!membersLoading && filteredMembers.length > 0 && filteredMembers.map((member) => (
          <MenuItem
            key={member.identifier}
            value={member.identifier}
            disabled={member.disabled}
            sx={{
              opacity: member.disabled ? 0.5 : 1,
              '&.Mui-disabled': {
                color: 'text.disabled',
              },
            }}
          >
            {member.identifier}
            {member.disabled && ' (No access)'}
          </MenuItem>
        ))}
      </Select>
      <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 1.5 }}>
        {error || helperText}
      </Typography>
    </FormControl>
  );
};

export default MemberIdentifierSelect;